import { useState, useRef, useCallback, useEffect } from 'react';
import { PhysicsObject, FunctionEntry, GraphSettings, SimulationState, ObjectPreset } from '@/types/simulation';
import { stepPhysics } from '@/utils/physics';
import { validateExpression } from '@/utils/mathParser';

const DEFAULT_GRAPH_SETTINGS: GraphSettings = {
  xMin: -10,
  xMax: 10,
  yMin: -6,
  yMax: 6,
  gridVisible: true,
  gridStep: 1,
  customLines: [],
  customSets: [],
  customIntersections: [],
};

const DEFAULT_SIM_STATE: SimulationState = {
  running: false,
  speed: 1,
  showTrails: true,
  time: 0,
  gravity: 9.8,
  objectCollisions: true,
};

const OBJECT_COLORS = [
  '#ff6b35', '#00d9ff', '#ffd700', '#ff4da6', '#7fff00', '#ff8c00',
];

export function useSimulation(canvasWidth: number, canvasHeight: number) {
  const [functions, setFunctions] = useState<FunctionEntry[]>([
    { id: '1', expression: 'sin(x)', color: '#00d9ff', visible: true },
  ]);
  const [graphSettings, setGraphSettings] = useState<GraphSettings>(DEFAULT_GRAPH_SETTINGS);
  const [simState, setSimState] = useState<SimulationState>(DEFAULT_SIM_STATE);
  const [objects, setObjects] = useState<PhysicsObject[]>([]);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [presets, setPresets] = useState<ObjectPreset[]>(() => {
    try {
      const raw = localStorage.getItem('kg_presets');
      return raw ? (JSON.parse(raw) as ObjectPreset[]) : [];
    } catch {
      return [];
    }
  });

  const animFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const objectsRef = useRef<PhysicsObject[]>(objects);
  const simStateRef = useRef<SimulationState>(simState);
  const functionsRef = useRef<FunctionEntry[]>(functions);
  const graphSettingsRef = useRef<GraphSettings>(graphSettings);

  useEffect(() => { objectsRef.current = objects; }, [objects]);
  useEffect(() => { simStateRef.current = simState; }, [simState]);
  useEffect(() => { functionsRef.current = functions; }, [functions]);
  useEffect(() => { graphSettingsRef.current = graphSettings; }, [graphSettings]);

  const addFunction = useCallback(() => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const color = OBJECT_COLORS[Math.floor(Math.random() * OBJECT_COLORS.length)];
    setFunctions((prev) => [...prev, { id, expression: '', color, visible: true }]);
  }, []);

  const addFunctionWithData = useCallback((data: Omit<FunctionEntry, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const err = validateExpression(data.expression);
    setFunctions((prev) => [
      ...prev,
      { ...data, id, error: err ?? undefined },
    ]);
  }, []);

  const updateFunction = useCallback((id: string, updates: Partial<FunctionEntry>) => {
    setFunctions((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const updated = { ...f, ...updates };
        if (updates.expression !== undefined) {
          const err = validateExpression(updates.expression);
          updated.error = err ?? undefined;
        }
        return updated;
      })
    );
  }, []);

  const removeFunction = useCallback((id: string) => {
    setFunctions((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const addObject = useCallback((partialObj?: Partial<PhysicsObject>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const color = OBJECT_COLORS[objects.length % OBJECT_COLORS.length];
    const newObj: PhysicsObject = {
      id,
      shape: 'circle',
      size: 0.75,   // ワールド単位の直径（半径0.375）
      mass: 1,
      restitution: 0.6,
      friction: 0.3,
      x: 0,
      y: 6,
      vx: 0,
      vy: 0,
      angularVelocity: 0,
      angle: 0,
      color,
      trail: [],
      ...partialObj,
    };
    setObjects((prev) => [...prev, newObj]);
    setSelectedObjectId(id);
    return id;
  }, [objects.length]);

  const updateObject = useCallback((id: string, updates: Partial<PhysicsObject>) => {
    setObjects((prev) => prev.map((o) => (o.id === id ? { ...o, ...updates } : o)));
  }, []);

  const removeObject = useCallback((id: string) => {
    setObjects((prev) => prev.filter((o) => o.id !== id));
    setSelectedObjectId((prev) => (prev === id ? null : prev));
  }, []);

  const resetSimulation = useCallback(() => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    lastTimeRef.current = null;
    setObjects((prev) =>
      prev.map((o) => ({ ...o, vx: 0, vy: 0, angularVelocity: 0, trail: [] }))
    );
    setSimState((prev) => ({ ...prev, running: false, time: 0 }));
  }, []);

  // 全体リセット: 全データをデフォルトに戻す
  const resetAll = useCallback(() => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    lastTimeRef.current = null;
    setFunctions([{ id: '1', expression: 'sin(x)', color: '#00d9ff', visible: true }]);
    setGraphSettings(DEFAULT_GRAPH_SETTINGS);
    setSimState(DEFAULT_SIM_STATE);
    setObjects([]);
    setSelectedObjectId(null);
  }, []);

  const tick = useCallback((timestamp: number) => {
    const state = simStateRef.current;
    if (!state.running) return;

    if (lastTimeRef.current === null) {
      lastTimeRef.current = timestamp;
    }
    const rawDt = (timestamp - lastTimeRef.current) / 1000;
    lastTimeRef.current = timestamp;
    // dtを小さく上限してスパイクを防ぐ。物体数が多い時はさらに小さくする
    const nObj = objectsRef.current.length;
    const dtCap = nObj >= 3 ? 0.025 : 0.033;
    const dt = Math.min(rawDt, dtCap) * state.speed;

    const stepped = stepPhysics(
      objectsRef.current,
      functionsRef.current,
      graphSettingsRef.current,
      state.gravity,
      dt,
      canvasWidth,
      canvasHeight,
      state.objectCollisions
    );
    // 一度の setState でまとめて更新（React 18 automatic batching）
    setObjects(stepped);
    setSimState((prev) => ({ ...prev, time: prev.time + dt }));

    animFrameRef.current = requestAnimationFrame(tick);
  }, [canvasWidth, canvasHeight]);

  const startSimulation = useCallback(() => {
    lastTimeRef.current = null;
    setSimState((prev) => ({ ...prev, running: true }));
  }, []);

  const pauseSimulation = useCallback(() => {
    setSimState((prev) => ({ ...prev, running: false }));
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (simState.running) {
      animFrameRef.current = requestAnimationFrame(tick);
    } else {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    }
    return () => {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [simState.running, tick]);

  const selectedObject = objects.find((o) => o.id === selectedObjectId) ?? null;

  // ---- Presets ----
  const savePreset = useCallback((name: string) => {
    const preset: ObjectPreset = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name,
      createdAt: Date.now(),
      objects: objectsRef.current.map((o) => ({
        id: o.id,
        shape: o.shape,
        size: o.size,
        mass: o.mass,
        restitution: o.restitution,
        friction: o.friction,
        x: o.x,
        y: o.y,
        vx: o.vx,
        vy: o.vy,
        color: o.color,
      })),
    };
    setPresets((prev) => {
      const next = [preset, ...prev].slice(0, 20);
      try { localStorage.setItem('kg_presets', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    return preset.id;
  }, []);

  const loadPreset = useCallback((presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    // Stop simulation first
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    lastTimeRef.current = null;
    const restored: PhysicsObject[] = preset.objects.map((snap) => ({
      ...snap,
      angularVelocity: 0,
      angle: 0,
      trail: [],
    }));
    setObjects(restored);
    setSimState((prev) => ({ ...prev, running: false, time: 0 }));
    if (restored.length > 0) setSelectedObjectId(restored[0].id);
  }, [presets]);

  const deletePreset = useCallback((presetId: string) => {
    setPresets((prev) => {
      const next = prev.filter((p) => p.id !== presetId);
      try { localStorage.setItem('kg_presets', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return {
    functions,
    graphSettings,
    simState,
    objects,
    selectedObjectId,
    selectedObject,
    addFunction,
    addFunctionWithData,
    updateFunction,
    removeFunction,
    setGraphSettings,
    setSimState,
    addObject,
    updateObject,
    removeObject,
    resetSimulation,
    resetAll,
    startSimulation,
    pauseSimulation,
    setSelectedObjectId,
    presets,
    savePreset,
    loadPreset,
    deletePreset,
  };
}
